import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { offersAPI, peekCache } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import MultiSelectFilter from '../components/MultiSelectFilter';
import { availabilityBadge, ORDINARY_AVAILABILITY } from '../styles/layout';
import { useChartColors } from '../contexts/ThemeContext';
import {
    PageHeader, SearchField, Switch, Pager, Alert, Tile, Modal, Icon, SourceAvatar,
    sourceColor, fmtNum, fmtMoney, timeAgo, syncServerClock, seasonLabel,
} from '../ui';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const PAGE_SIZE = 50;
const EMPTY_FILTERS = { brand: [], model: [], season: [], width: [], profil: [], diametr: [] };
const CHANGE_PERIODS = [
    { value: '', label: 'Будь-коли' },
    { value: '1', label: 'Змінились сьогодні' },
    { value: '3', label: 'За 3 дні' },
    { value: '7', label: 'За 7 днів' },
];

// Единая страница источника-конкурента вместо DatabaseTiresPage/UkrshinaPage/
// блока браузинга в CompetitorsPage — источник задаётся URL-параметром и берётся
// из /offers/sources, а не хардкодится в компоненте.
const CompetitorSourcePage = () => {
    const { source } = useParams();

    const cachedSources = peekCache('/offers/sources', {})?.sources || [];
    const [sourceMeta, setSourceMeta] = useState(cachedSources.find(s => s.source_key === source) || null);
    const [allSources, setAllSources] = useState(cachedSources);
    const [stats, setStats] = useState(() => peekCache('/offers/stats', { source }) || null);
    const [task, setTask] = useState(null);
    const [status, setStatus] = useState(null);
    const [collectError, setCollectError] = useState(null);

    const [offers, setOffers] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [withPriceOnly, setWithPriceOnly] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [selectedOffer, setSelectedOffer] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(null);

    const [facets, setFacets] = useState(null);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [changedWithin, setChangedWithin] = useState('');
    const [sort, setSort] = useState({ by: 'name', order: 'asc' });

    const loadMeta = useCallback(async () => {
        try {
            const { sources } = await offersAPI.sources();
            setAllSources(sources || []);
            setSourceMeta((sources || []).find(s => s.source_key === source) || null);
        } catch (e) { /* источник ещё не в реестре — ок */ }
    }, [source]);

    const loadStats = useCallback(async (opts) => {
        try {
            const data = await offersAPI.stats(source, opts);
            syncServerClock(data.now);
            setStats(data);
        } catch (e) { /* ok */ }
    }, [source]);

    const loadFacets = useCallback(async () => {
        try { setFacets(await offersAPI.facets(source)); } catch (e) { /* ok */ }
    }, [source]);

    // Спільна для списку й експорту: та сама вибірка, лише інший limit/offset.
    const buildParams = useCallback((extra = {}) => {
        const params = {
            source, search, sort_by: sort.by, sort_order: sort.order, ...extra,
        };
        if (withPriceOnly) params.has_price = true;
        if (changedWithin) params.changed_within_days = changedWithin;
        // Мультивибір їде рядком через кому — бекенд розбирає його сам.
        Object.entries(filters).forEach(([key, values]) => {
            if (values.length) params[key] = values.join(',');
        });
        return params;
    }, [source, search, withPriceOnly, changedWithin, filters, sort]);

    const loadOffers = useCallback(async () => {
        setListLoading(true);
        try {
            const params = buildParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
            const data = await offersAPI.list(params);
            setOffers(data.offers || []);
            setTotal(data.total || 0);
        } catch (e) {
            setOffers([]); setTotal(0);
        } finally {
            setListLoading(false);
        }
    }, [buildParams, page]);

    const handleExport = async () => {
        setExporting(true);
        setExportError(null);
        try {
            // Той самий фільтр, що на екрані, але без пагінації — весь набір
            // одним запитом (бек обмежує це 200 000 рядків, з запасом).
            const params = buildParams({ limit: Math.max(total, 1), offset: 0 });
            const data = await offersAPI.list(params);
            const rows = (data.offers || []).map(o => ({
                // ID конкурента — саме те значення, яке йде в TireMapping.competitor_id
                // при зіставленні на сторінці /mapping. Без нього експорт годиться
                // лише для перегляду, а не для побудови файлу зіставлень.
                'ID': o.external_id || '',
                'Бренд': o.brand || '',
                'Назва': o.name || '',
                'Ширина': o.width || '',
                'Профіль': o.profil || '',
                'Діаметр': o.diametr || '',
                'Сезон': o.season || '',
                'Ціна': o.price ?? '',
                'Наявність': o.availability || (o.price == null ? 'немає в наявності' : ''),
                'URL': o.url || '',
            }));
            const ws = XLSX.utils.json_to_sheet(rows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, source);
            XLSX.writeFile(wb, `${source}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (e) {
            setExportError('Не вдалося сформувати файл');
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        setPage(0); setSearch(''); setFilters(EMPTY_FILTERS); setChangedWithin('');
    }, [source]);
    useEffect(() => { loadMeta(); loadStats(); loadFacets(); }, [loadMeta, loadStats, loadFacets]);
    // Затримка — лише поки користувач друкує в пошуку.
    const lastSearch = useRef(search);
    useEffect(() => {
        const typing = lastSearch.current !== search;
        lastSearch.current = search;
        const t = setTimeout(loadOffers, typing ? 300 : 0);
        return () => clearTimeout(t);
    }, [loadOffers, search]);

    const setFilter = (key, values) => {
        setFilters(prev => ({ ...prev, [key]: values }));
        setPage(0);
    };
    const activeFilterCount = Object.values(filters).reduce((n, v) => n + v.length, 0);
    const toggleSort = (key) => {
        setSort(prev => ({
            by: key,
            order: prev.by === key && prev.order === 'asc' ? 'desc' : 'asc',
        }));
        setPage(0);
    };
    const sortIcon = (key) => (sort.by !== key ? '' : sort.order === 'asc' ? ' ↑' : ' ↓');

    useEffect(() => {
        if (!task) return;
        const iv = setInterval(async () => {
            try {
                const s = await offersAPI.taskStatus(task.task_id);
                setStatus(s);
                if (s.status === 'completed' || s.status === 'failed') {
                    clearInterval(iv);
                    setTask(null);
                    if (s.status === 'failed') setCollectError(s.error || 'Помилка збору');
                    offersAPI.afterFetch();
                    await loadStats({ fresh: true });
                    await loadOffers();
                }
            } catch (e) { /* ignore transient */ }
        }, 2000);
        return () => clearInterval(iv);
    }, [task, loadStats, loadOffers]);

    const startCollect = async () => {
        setCollectError(null); setStatus(null);
        try {
            const res = await offersAPI.fetch(source);
            setTask(res);
            setStatus({ status: 'started', progress: 0 });
        } catch (e) {
            setCollectError('Не вдалося запустити збір');
        }
    };

    const running = !!task;
    const progress = status?.progress ?? 0;
    const title = sourceMeta?.title || source;

    const sourceIndex = Math.max(0, allSources.findIndex(x => x.source_key === source));
    const withPricePct = stats?.total ? Math.round(stats.with_price / stats.total * 100) : null;

    return (
        <div className="ui-page">
            <PageHeader
                back={<Link to="/competitors" className="ui-back"><Icon name="arrowLeft" size={16} /> Усі конкуренти</Link>}
                title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 14 }}>
                    <SourceAvatar sourceKey={source} title={title} size={44} index={sourceIndex} />{title}
                </span>}
                description={sourceMeta?.base_url && (
                    <a href={sourceMeta.base_url} target="_blank" rel="noreferrer">
                        {sourceMeta.base_url.replace(/^https?:\/\//, '')} <Icon name="external" size={14} />
                    </a>
                )}
                actions={
                    <button className="ui-btn ui-btn--primary" onClick={startCollect} disabled={running}>
                        <Icon name="parser" />
                        {running ? 'Збір триває…' : 'Зібрати зараз'}
                    </button>
                }
            />

            {/* Швидке перемикання між джерелами — без повернення до списку */}
            {allSources.length > 1 && (
                <div className="ui-toolbar">
                    {allSources.map(s => (
                        <Link key={s.source_key} to={`/competitors/${s.source_key}`}
                              className={`ui-pill${s.source_key === source ? ' is-on' : ''}`}>
                            {s.title}
                        </Link>
                    ))}
                </div>
            )}

            <section className="ui-tiles">
                <Tile label="Товарів у базі" value={fmtNum(stats?.total ?? 0)} />
                <Tile label="З актуальною ціною" value={fmtNum(stats?.with_price ?? 0)}
                      chip={withPricePct != null ? `${withPricePct}%` : null} chipKind="good" />
                <Tile label="Останнє оновлення" value={stats?.last_updated ? timeAgo(stats.last_updated) : '—'}
                      note={stats?.last_updated ? stats.last_updated.slice(0, 16) : 'ще не збирали'} />
                {stats?.seasons && Object.keys(stats.seasons).length > 0 && (
                    <div className="ui-tile">
                        <span className="ui-tile-label">По сезонах</span>
                        <span style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
                            {Object.entries(stats.seasons).map(([s, n]) => (
                                <span key={s} style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                                    <span className="muted-2">{s === 'unknown' ? 'Не вказано' : seasonLabel(s)}</span><b className="num">{fmtNum(n)}</b>
                                </span>
                            ))}
                        </span>
                    </div>
                )}
            </section>

            {(status || collectError) && (
                <section className="ui-card">
                    {status && (
                        <>
                            <div className="ui-progress"><div style={{ width: `${progress}%` }} /></div>
                            <div className="muted-2" style={{ fontSize: 13, marginTop: 10 }}>
                                {status.status === 'scraping' && (
                                    <>
                                        Збір: {status.done ?? 0}/{status.total ?? '…'} стор.
                                        {status.total > 0 && ` (${((status.done / status.total) * 100).toFixed(1)}%)`}
                                        {' · знайдено '}{fmtNum(status.total_tires ?? 0)}
                                        {status.total > 300 && <span className="muted"> · великий каталог, це надовго</span>}
                                    </>
                                )}
                                {status.status === 'saving' && <>Збереження: {fmtNum(status.saved ?? 0)}/{fmtNum(status.total_tires ?? 0)}</>}
                                {status.status === 'completed' && <>Готово: {fmtNum(status.saved ?? status.total_tires)} збережено{status.price_changes != null && <>, змін цін: {fmtNum(status.price_changes)}</>}</>}
                                {status.status === 'failed' && <span style={{ color: 'var(--bad)' }}>Помилка: {status.error}</span>}
                                {status.status === 'started' && <>Запуск…</>}
                            </div>
                        </>
                    )}
                    {collectError && <div style={{ color: 'var(--bad)', marginTop: status ? 10 : 0 }}>{collectError}</div>}
                </section>
            )}

            <section className="ui-card ui-card--flush">
                <div className="ui-card-head">
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                        <h2 className="ui-section-title" style={{ fontSize: 20 }}>Товари</h2>
                        <span className="muted num" style={{ fontSize: 14 }}>{fmtNum(total)}</span>
                    </div>
                    <div className="ui-toolbar">
                        <Switch checked={withPriceOnly} onChange={v => { setWithPriceOnly(v); setPage(0); }}>
                            Тільки з ціною
                        </Switch>
                        <SearchField value={search} onChange={v => { setPage(0); setSearch(v); }}
                                     placeholder="Пошук за назвою" style={{ minWidth: 260 }} />
                        <button className="ui-btn" onClick={handleExport} disabled={exporting || total === 0}>
                            <Icon name="download" />
                            {exporting ? 'Формую…' : `Excel · ${fmtNum(total)}`}
                        </button>
                    </div>
                </div>
                {exportError && <div style={{ padding: '0 24px' }}><Alert kind="error">{exportError}</Alert></div>}

                <div className="ui-toolbar" style={{ padding: '4px 24px 12px' }}>
                    <MultiSelectFilter label="Бренд" searchable
                        options={facets?.brands || []} selected={filters.brand}
                        onChange={v => setFilter('brand', v)} />
                    <MultiSelectFilter label="Модель" searchable
                        options={facets?.models || []} selected={filters.model}
                        onChange={v => setFilter('model', v)} />
                    <MultiSelectFilter label="Сезон" renderOption={seasonLabel}
                        options={facets?.seasons || []} selected={filters.season}
                        onChange={v => setFilter('season', v)} />
                    <MultiSelectFilter label="Ширина"
                        options={facets?.widths || []} selected={filters.width}
                        onChange={v => setFilter('width', v)} />
                    <MultiSelectFilter label="Профіль"
                        options={facets?.profils || []} selected={filters.profil}
                        onChange={v => setFilter('profil', v)} />
                    <MultiSelectFilter label="Діаметр"
                        options={facets?.diametrs || []} selected={filters.diametr}
                        onChange={v => setFilter('diametr', v)} />

                    <select value={changedWithin}
                            onChange={e => { setChangedWithin(e.target.value); setPage(0); }}
                            className={`ui-select-pill${changedWithin ? ' is-on' : ''}`} aria-label="Зміна ціни">
                        {CHANGE_PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>

                    {(activeFilterCount > 0 || changedWithin) && (
                        <button className="ui-btn ui-btn--ghost ui-btn--sm"
                                onClick={() => { setFilters(EMPTY_FILTERS); setChangedWithin(''); setPage(0); }}>
                            Скинути фільтри{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ''}
                        </button>
                    )}
                </div>

                {listLoading && offers.length === 0 ? <LoadingSpinner /> : (
                    <div className="ui-table-wrap" style={{ opacity: listLoading ? 0.5 : 1, transition: 'opacity .2s' }}>
                        <table className="ui-table">
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => toggleSort('brand')}>Бренд{sortIcon('brand')}</th>
                                    <th className="sortable" onClick={() => toggleSort('name')}>Назва{sortIcon('name')}</th>
                                    <th>Розмір</th>
                                    <th className="sortable" onClick={() => toggleSort('season')}>Сезон{sortIcon('season')}</th>
                                    <th className="sortable r" onClick={() => toggleSort('price')}>Ціна{sortIcon('price')}</th>
                                    <th>Зміна</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {offers.length === 0 ? (
                                    <tr><td colSpan={7} className="muted">
                                        {activeFilterCount > 0 || changedWithin || search
                                            ? 'Нічого не знайдено за обраними фільтрами.'
                                            : 'Порожньо — запусти збір кнопкою вгорі.'}
                                    </td></tr>
                                ) : offers.map(o => (
                                    <tr key={o.id}>
                                        <td className="nowrap muted-2">{o.brand || '—'}</td>
                                        <td style={{ minWidth: 260 }}>
                                            {o.url ? <a href={o.url} target="_blank" rel="noreferrer" style={{ color: 'var(--text)', fontWeight: 600 }}>{o.name}</a>
                                                   : <span style={{ fontWeight: 600 }}>{o.name}</span>}
                                        </td>
                                        <td className="nowrap num">{o.width}/{o.profil} {o.diametr}</td>
                                        <td className="nowrap muted-2">{seasonLabel(o.season) || '—'}</td>
                                        <td className="r nowrap">
                                            {o.price != null
                                                ? <b style={{ fontSize: 15 }}>{fmtMoney(o.price)}</b>
                                                : <span className="muted" style={{ fontSize: 13 }}>немає в наявності</span>}
                                            {o.price != null && o.availability && o.availability !== ORDINARY_AVAILABILITY && (
                                                <span style={availabilityBadge}>{o.availability}</span>
                                            )}
                                        </td>
                                        <td className="nowrap">
                                            {o.price_delta != null && o.price_delta !== 0 ? (
                                                <span className={`ui-chip ui-chip--${o.price_delta > 0 ? 'up' : 'down'}`}
                                                      title={`Було ${fmtMoney(o.prev_price)} · ${o.changed_at}`}>
                                                    {o.price_delta > 0 ? '+' : '−'}{fmtNum(Math.abs(o.price_delta))} ₴
                                                </span>
                                            ) : <span className="muted">—</span>}
                                        </td>
                                        <td className="r">
                                            <button className="ui-btn ui-btn--sm" onClick={() => setSelectedOffer(o)}>
                                                <Icon name="history" size={16} /> Історія
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pager page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} />
            </section>

            {selectedOffer && (
                <OfferHistoryModal offer={selectedOffer} color={sourceColor(source, sourceIndex)}
                                   onClose={() => setSelectedOffer(null)} />
            )}
        </div>
    );
};

const OfferHistoryModal = ({ offer, color, onClose }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const colors = useChartColors();

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const data = await offersAPI.priceHistory(offer.id);
                setHistory(data.price_history || []);
            } catch (e) { /* ignore */ }
            finally { setLoading(false); }
        })();
    }, [offer]);

    const toTime = (s) => new Date(s.replace(' ', 'T')).getTime();
    const chartData = history.map(p => ({ t: toTime(p.recorded_at), price: p.price }));
    // В історію пишеться лише зміна ціни, тож без цієї точки графік обривався
    // на даті останньої зміни — і свіжий збір із тією самою ціною виглядав
    // так, ніби даних за останні дні немає.
    const lastChange = history.length ? history[history.length - 1].recorded_at : null;
    const checkedAt = offer.last_seen_at;
    if (lastChange && checkedAt && checkedAt > lastChange) {
        chartData.push({ t: toTime(checkedAt), price: history[history.length - 1].price, checked: true });
    }
    const prices = history.map(p => p.price).filter(p => p != null);
    const minP = prices.length ? Math.min(...prices) : null;
    const maxP = prices.length ? Math.max(...prices) : null;
    const fmtTick = (t) => {
        const d = new Date(t);
        return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    };
    const statTile = { background: 'var(--surface-2)', padding: '16px 18px' };

    return (
        <Modal
            onClose={onClose}
            width={720}
            title={offer.name}
            head={<div className="ui-toolbar" style={{ gap: 8, marginBottom: 12 }}>
                {offer.brand && <span className="ui-chip">{offer.brand}</span>}
                <span className="ui-chip num">{offer.width}/{offer.profil} {offer.diametr}</span>
                {offer.season && <span className="ui-chip">{seasonLabel(offer.season)}</span>}
            </div>}
        >
            {loading ? <LoadingSpinner /> : history.length === 0 ? (
                <p className="muted">Історія цін поки порожня.</p>
            ) : (
                <>
                    <div className="ui-tiles" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', marginBottom: 20 }}>
                        {[['Поточна', offer.price], ['Мінімум', minP], ['Максимум', maxP]].map(([label, v]) => (
                            <div key={label} className="ui-tile" style={statTile}>
                                <span className="ui-tile-label">{label}</span>
                                <span className="ui-tile-value" style={{ fontSize: 24 }}>{fmtMoney(v)}</span>
                            </div>
                        ))}
                        <div className="ui-tile" style={statTile}>
                            <span className="ui-tile-label">Змін</span>
                            <span className="ui-tile-value" style={{ fontSize: 24 }}>{history.length}</span>
                        </div>
                    </div>
                    <div style={{ width: '100%', height: 260 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid vertical={false} stroke={colors.line} />
                                <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                                       tickFormatter={fmtTick} tick={{ fontSize: 12, fill: colors.text3 }}
                                       axisLine={false} tickLine={false} minTickGap={24} />
                                <YAxis tick={{ fontSize: 12, fill: colors.text3 }} domain={['auto', 'auto']}
                                       tickFormatter={v => fmtNum(v)} axisLine={false} tickLine={false} width={60} />
                                <Tooltip
                                    contentStyle={{ background: colors.surface3, border: 0, borderRadius: 14, color: colors.text }}
                                    labelStyle={{ color: colors.text, fontWeight: 700 }}
                                    itemStyle={{ color: colors.text2 }}
                                    labelFormatter={t => new Date(t).toLocaleString('uk-UA', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' })}
                                    formatter={(v, _name, item) => [v != null ? fmtMoney(v) : 'немає', item.payload.checked ? 'Перевірено, без змін' : 'Ціна']} />
                                {/* Ціна тримається до наступної зміни — сходинка, а не плавна крива між точками. */}
                                <Line type="stepAfter" dataKey="price" stroke={color} strokeWidth={3}
                                      dot={{ r: 3, fill: color, strokeWidth: 0 }} isAnimationActive={false} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="ui-toolbar muted" style={{ fontSize: 13, marginTop: 12, gap: 16 }}>
                        <span>Остання зміна: {lastChange ? lastChange.slice(0, 16) : '—'}</span>
                        <span>Перевірено: {checkedAt ? checkedAt.slice(0, 16) : '—'}</span>
                    </div>
                </>
            )}
        </Modal>
    );
};

export default CompetitorSourcePage;
